import React, { useContext, useState, useEffect, useMemo } from 'react';
import { DropDownList } from 'components/formInputs/dropDownList';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Input } from 'components/formInputs/input';
import { Config } from 'configs';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { PageTitle } from 'components/layouts/pageTitle';
import { v4 as uuidv4 } from 'uuid';
import { joinClassNames } from 'utils/cssHelper';
import { ConfirmationDialog, ConfirmationDialogTypes } from 'components/lists/confirmationDialog';

export const TablesAddEditForm = ({ tableData, isEdit, initialRestaurant }) => {
  const navigate = useNavigate();

  const { state: companyState, addNew, editById, deleteById, getCompanyRestaurants } = useContext(CompanyContext);
  const { state: auth } = useContext(AuthContext);
  const [selectedType, setSelectedType] = useState('Simple');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const schema = yup.object().shape({
    _id: yup.string().default(tableData?._id),
    qr: yup.string().default(uuidv4()),
    area_name: yup.string().default(tableData?.area_name),
    name: yup.string().default(tableData?.name),
    number: yup
      .string()
      .required(
        'Please enter a number for the table. This is the number that will be displayed on the table.',
      )
      .matches(/^\d+$/, 'Please enter a number for the table. This is the number that will be displayed on the table.')
      .default(tableData?.number),
    restaurant: yup.string().default(tableData?.restaurant || initialRestaurant),
    type: yup
      .string()
      .required()
      .default(tableData?.type || 'Simple'),
    // eslint-disable-next-line no-restricted-globals
    rkeeper_code: yup.number().transform((value) => (isNaN(value) ? undefined : value)).nullable(),
    external_id: yup.string().default(tableData?.external_id),
  });

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: useMemo(() => {
      return schema.default();
    }, [schema]),
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setValue('type', selectedType);
  }, [selectedType, setValue]);

  useEffect(() => {
    setSelectedType(tableData?.type || 'Simple');
    reset(tableData);
  }, [tableData, reset]);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  const onSubmit = async (data) => {
    const sumbitData = { ...data, company: auth.company?.company, is_manual: true, hidden: false };

    if (!isEdit) {
      await addNew(
        {
          collectionName: Config.DATA.COLLECTIONS.TABLES,
          data: sumbitData,
          token: auth.token,
          resourceV2: 'AdminTable',
          companyIdForV2: auth.company?.company,
        },
        () => navigate('/settings/tables'),
      );
    } else {
      await editById(
        {
          collectionName: Config.DATA.COLLECTIONS.TABLES,
          id: data._id,
          updateClause: `{ $set: { is_manual: true, number: '${data.number}', area_name: '${data.area_name || ''}', name: '${data.name || ''}', rkeeper_code: ${data.rkeeper_code || null}, external_id: '${data.external_id || ''}' }}`,
          data: sumbitData,
          token: auth.token,
          resourceV2: 'AdminTable',
          updateTypeV2: 'General',
          updateDataV2: {
            is_manual: true,
            number: data.number,
            area_name: data.area_name || '',
            name: data.name || '',
            rkeeper_code: data.rkeeper_code || null,
            external_id: data.external_id || '',
          },
          companyIdForV2: auth.company?.company,
        },
        () => navigate('/settings/tables'),
      );
    }
  };

  const onDelete = async () => {
    await deleteById({
      collectionName: Config.DATA.COLLECTIONS.TABLES,
      token: auth.token,
      id: tableData._id,
      resourceV2: 'AdminTable',
      companyIdForV2: auth.company?.company,
    }, () => navigate('/settings/tables'));
  };

  const onError = (data) => console.log(data);

  return (
    <>
      <PageTitle title="Table" breadcrumbs={[{ path: '/settings/tables', title: 'Tables' }]} />
      <div className="container mx-auto px-2 md:px-6 lg:px-8">
        <div className="w-full">
          <form onSubmit={handleSubmit(onSubmit, onError)} action="#" method="POST">
            <div className="shadow sm:rounded-md sm:overflow-hidden">
              <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                <div>
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {isEdit ? ` Editing table: ${tableData?.number}` : 'Add new table'}
                  </h3>
                </div>

                <div className="grid grid-cols-6 gap-6">
                  <div className="col-span-6 sm:col-span-3">
                    <DropDownList
                      {...register('restaurant')}
                      showLabel
                      id="restaurant"
                      name="restaurant"
                      label="Restaurant*"
                      options={companyState.restaurants?.map((restaurant) => ({
                        label: restaurant.name,
                        value: restaurant._id,
                      }))}
                      className={joinClassNames(
                        isEdit ? 'bg-gray-50' : '',
                        'mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md',
                      )}
                      disabled={isEdit}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="number" className="block text-sm font-medium text-gray-700">
                      Table number*
                    </label>
                    <div className="mt-1 relative text-sm text-gray-500">
                      <Input
                        {...register('number')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        name="number"
                        id="number"
                        type="text"
                        errorMessage={errors?.number}
                      />
                    </div>
                    {errors?.number && (
                      <p className="mt-2 text-sm text-red-600" id="number-error">
                        {errors?.number.message}
                      </p>
                    )}
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="area_name" className="block text-sm font-medium text-gray-700">
                      Section name
                    </label>
                    <div className="mt-1 relative text-sm text-gray-500">
                      <Input
                        {...register('area_name')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        name="area_name"
                        id="area_name"
                        type="text"
                        errorMessage={errors?.area_name}
                      />
                    </div>
                    {errors?.area_name && (
                    <p className="mt-2 text-sm text-red-600" id="area_name-error">
                      {errors?.area_name.message}
                    </p>
                    )}
                  </div>

                  <div className="col-span-6 sm:col-span-3">
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Table name
                    </label>
                    <div className="mt-1 relative text-sm text-gray-500">
                      <Input
                        {...register('name')}
                        className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        name="name"
                        id="name"
                        type="text"
                        errorMessage={errors?.name}
                      />
                    </div>
                    {errors?.name && (
                      <p className="mt-2 text-sm text-red-600" id="name-error">
                        {errors?.name.message}
                      </p>
                    )}
                  </div>

                  <div className="col-span-6">
                    <fieldset className="mt-6">
                      <div className="mb-6">
                        <legend className="text-base font-medium text-gray-900">External ID</legend>
                        <p className="text-sm text-gray-500 mt-1">
                          Table identifiers from integration (optional)
                        </p>
                      </div>
                      <div className="grid grid-cols-6 gap-6">
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="rkeeper_code" className="block text-sm font-medium text-gray-700">
                            External table code
                          </label>
                          <div className="mt-1 relative text-sm text-gray-500">
                            <Input
                              {...register('rkeeper_code')}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              name="rkeeper_code"
                              id="rkeeper_code"
                              type="number"
                              errorMessage={errors?.rkeeper_code}
                            />
                          </div>
                          {errors?.rkeeper_code && (
                          <p className="mt-2 text-sm text-red-600" id="rkeeper_code-error">
                            {errors?.rkeeper_code.message}
                          </p>
                          )}
                        </div>
                        <div className="col-span-6 sm:col-span-3">
                          <label htmlFor="external_id" className="block text-sm font-medium text-gray-700">
                            External table ID
                          </label>
                          <div className="mt-1 relative text-sm text-gray-500">
                            <Input
                              {...register('external_id')}
                              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              name="external_id"
                              id="external_id"
                              type="text"
                              errorMessage={errors?.external_id}
                            />
                          </div>
                          {errors?.external_id && (
                          <p className="mt-2 text-sm text-red-600" id="external_id-error">
                            {errors?.external_id.message}
                          </p>
                          )}
                        </div>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
              <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-between items-start">
                {isEdit && (
                <button
                  type="button"
                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => setIsDeleteDialogOpen(true)}
                >
                  Delete
                </button>
                )}
                {!isEdit && <div />}
                <div className="text-right">
                  <button
                    type="button"
                    className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    onClick={() => navigate('/settings/tables')}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ConfirmationDialog
        type={ConfirmationDialogTypes.DELETE}
        title="Delete table"
        description="Are you sure you want to delete the table?"
        isOpen={isDeleteDialogOpen}
        closeModal={() => setIsDeleteDialogOpen(false)}
        onSubmit={onDelete}
      />
    </>
  );
};
